import {
  CurecallDatePicker,
  CurecallGenderRadio,
  CurecallInput,
  CurecallPhoneInput,
  CurecallSelect,
  CurecallSelectItem,
  CurecallSelectItems,
  CurecallToggle,
  ECurecallSize,
  PeopleIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { type ReactNode, Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { FieldErrors, useFormContext } from 'react-hook-form'
import SectionTitle from 'src/components/section-title/section-title.component'
import SelectMedicalStaffMember from 'src/components/select-medical-staff-member/select-medical-staff-member.component'
import { EPatientGender, TPatient } from 'src/types/patient'
import { capitalizeFirstname } from 'src/utils/string-formatter.util'
import { twMerge } from 'src/utils/tw-merge.util'
import { AdminOnlyBadge } from 'src/components/admin-only-badge.component'

import { useCurrentProject } from 'src/providers/current-project.provider'
import { TCurrentUser } from 'src/types/session'
import { ELocale, TPatientLocale } from 'src/types/locales'

export type IAddNewPatientFormValues = {
  isTestAccount: boolean
  gender: string
  firstname: string
  lastname: string
  birthdate: string | null
  phone: string
  hasCaregiver: boolean
  MedicalStaffMemberId: number
  locale: TPatientLocale
  remoteId: string
}

export type TPatientFormComponentProps = {
  projectId: number
  currentUser?: TCurrentUser
  isTestAccount?: boolean
  setIsTestAccount?: Dispatch<SetStateAction<boolean>>
  hasCaregiver?: boolean
  setHasCareGiver?: Dispatch<SetStateAction<boolean>>
  errors?: FieldErrors<IAddNewPatientFormValues>
  patient?: Partial<TPatient>
  isEditPatient?: boolean
  isInDemoGroup?: boolean
  setIsInDemoGroup?: Dispatch<SetStateAction<boolean>>
}

export const parsePatientValues = (
  patient: IAddNewPatientFormValues,
  isTestAccount?: boolean,
  hasCareGiver?: boolean,
) => ({
  gender: patient.gender,
  firstname: patient.firstname,
  lastname: patient.lastname,
  phone: patient?.phone?.length ? patient.phone : null,
  birthdate: patient.birthdate,
  hasCaregiver: hasCareGiver,
  MedicalStaffMemberId: patient.MedicalStaffMemberId,
  locale: patient.locale,
  remoteId: patient.remoteId,
  isTestAccount,
})

type PatientLocaleOption = CurecallSelectItem<TPatientLocale>
enum EPatientFormField {
  GENDER = 'GENDER',
  FIRSTNAME = 'FIRSTNAME',
  LASTNAME = 'LASTNAME',
  BIRTH_DATE = 'BIRTH_DATE',
  PHONE = 'PHONE',
  LOCALE = 'LOCALE',
  REMOTE_ID = 'REMOTE_ID',
  MEDICAL_STAFF_MEMBER = 'MEDICAL_STAFF_MEMBER',
}

const PatientLocaleSelectItems: CurecallSelectItems<TPatientLocale> = [
  {
    label: 'components.patient.add-or-edit.patient-form.french',
    value: ELocale.FR,
  },
  {
    label: 'components.patient.add-or-edit.patient-form.english',
    value: ELocale.EN,
  },
  {
    label: 'components.patient.add-or-edit.patient-form.spanish',
    value: ELocale.ES,
  },
  {
    label: 'components.patient.add-or-edit.patient-form.polski',
    value: ELocale.PL,
  },
]

const TooltipFirstnameContent = () => {
  const { t } = useTranslation()
  return (
    <div className="c-w-96">
      {t('components.patient.add-or-edit.patient-form.tooltip-firstname', {
        br: (chunks: ReactNode) => <p>{chunks}</p>,
        boldText: (chunks: ReactNode) => <b>{chunks}</b>,
      })}
    </div>
  ) as unknown as string
}

const PatientFormComponent = ({
  projectId,
  currentUser,
  isTestAccount,
  setIsTestAccount,
  hasCaregiver,
  setHasCareGiver,
  errors,
  isEditPatient,
  patient,
  isInDemoGroup,
  setIsInDemoGroup,
}: TPatientFormComponentProps) => {
  const { t } = useTranslation()
  const selectedDefaultDateValue = patient?.birthdate || null
  const {
    register,
    setValue,
    watch,
    clearErrors,
    formState: { defaultValues },
  } = useFormContext<IAddNewPatientFormValues>()

  const [selectedDate, setSelectedDate] = useState<string | null>(selectedDefaultDateValue)
  const [displayCareGiver, setDisplayCareGiver] = useState(false)
  const { project } = useCurrentProject()
  const patientLocaleOptions = useMemo(
    () =>
      PatientLocaleSelectItems.filter(
        (item) =>
          !project?.settings?.patientLocales?.length || project.settings.patientLocales.includes(item.value),
      ).map((patientLocaleSelectItem: PatientLocaleOption) => ({
        label: t(patientLocaleSelectItem.label),
        value: patientLocaleSelectItem.value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project?.settings?.patientLocales],
  )

  const [selectedPatientLocale, setSelectedPatientLocale] = useState<PatientLocaleOption | undefined>(
    patientLocaleOptions.find(
      (patientLocale: PatientLocaleOption) =>
        patientLocale.value ===
        (project?.settings?.patientDefaultLocale || PatientLocaleSelectItems[0]?.value),
    ),
  )

  const genderOptions: Array<{ value: 'MALE' | 'FEMALE'; label: string }> = [
    { value: EPatientGender.MALE as 'MALE', label: t('components.patient.add-or-edit.patient-form.male') },
    {
      value: EPatientGender.FEMALE as 'FEMALE',
      label: t('components.patient.add-or-edit.patient-form.female'),
    },
  ]

  useEffect(() => {
    if (isEditPatient && selectedDefaultDateValue) {
      setSelectedDate(selectedDefaultDateValue)
    }
  }, [isEditPatient, selectedDefaultDateValue])

  useEffect(() => {
    const currenEPatientLocale = PatientLocaleSelectItems.find(
      (patientLocaleSelectItem: PatientLocaleOption) => patient?.locale === patientLocaleSelectItem.value,
    )
    if (patient?.locale) {
      handleChangePatientLocale({
        label: t(currenEPatientLocale?.label ?? ''),
        value: patient?.locale,
      })
    } else {
      selectedPatientLocale &&
        handleChangePatientLocale({
          label: selectedPatientLocale?.label,
          value: selectedPatientLocale?.value,
        })
    }
  }, [patient?.locale]) //eslint-disable-line

  const handleChangePatientLocale = (patientLocaleOption: PatientLocaleOption) => {
    setSelectedPatientLocale(patientLocaleOption)
    setValue('locale', patientLocaleOption.value)
    clearErrors('locale')
  }

  const isToBeDisplayed = (
    fieldName:
      | 'GENDER'
      | 'FIRSTNAME'
      | 'LASTNAME'
      | 'BIRTH_DATE'
      | 'PHONE'
      | 'LOCALE'
      | 'REMOTE_ID'
      | 'MEDICAL_STAFF_MEMBER',
  ) => {
    if (project?.settings?.patientCreationFields?.length) {
      return project.settings.patientCreationFields.find((item) => item.name === fieldName)
    }
    return true
  }
  const isRequiredField = (fieldName: EPatientFormField, defaultValue = true) => {
    if (project?.settings?.patientCreationFields?.length) {
      return project.settings.patientCreationFields.find((item) => item.name === fieldName)?.isRequired
    }
    return defaultValue
  }

  return (
    <div>
      {!isEditPatient && (
        <div className="c-flex c-flex-col c-gap-4 c-mb-4">
          <SectionTitle
            icon={<PeopleIcon className="c-w-5" />}
            sectionTitle={t('components.patient.add-or-edit.patient-form.patient-infos')}
          />
          {currentUser?.isAdminOrSales && (
            <div className="c-flex c-gap-10 c-mb-6">
              <div className="c-flex c-items-center c-gap-1">
                <AdminOnlyBadge />
                <CurecallToggle
                  size={ECurecallSize.LARGE}
                  leftLabel={t('components.patient.add-or-edit.patient-form.test-account')}
                  isEnabled={!!isTestAccount}
                  onChange={(isTestAccount: boolean) =>
                    setIsTestAccount && setIsTestAccount(!!currentUser?.isAdmin && isTestAccount)
                  }
                />
              </div>
              {setIsInDemoGroup && (
                <div className="c-flex c-items-center c-gap-1">
                  <AdminOnlyBadge />
                  <CurecallToggle
                    size={ECurecallSize.LARGE}
                    leftLabel={t('components.patient.add-or-edit.patient-form.add-to-demo-group')}
                    isEnabled={!!isInDemoGroup}
                    onChange={() => setIsInDemoGroup((prev) => !prev)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="c-flex c-flex-col c-gap-8">
        <div className="c-flex c-space-x-24">
          {isToBeDisplayed(EPatientFormField.GENDER) && (
            <div className="c-flex c-items-center">
              {genderOptions.map((genderOption) => (
                <span key={genderOption.value} className="c-h-12">
                  <CurecallGenderRadio
                    radioOption={genderOption}
                    isByDefaultChecked={watch('gender') === genderOption.value}
                    showAsterisk={!watch('gender')}
                    isDisabled={watch('gender') !== genderOption.value}
                    {...register('gender', {
                      required: t('components.patient.add-or-edit.patient-form.select-gender-error-message'),
                    })}
                    onChange={() => {
                      setValue('gender', genderOption.value)
                      clearErrors('gender')
                    }}
                  />
                </span>
              ))}
              {errors?.gender && (
                <span
                  role="alert"
                  className="c-font-nexa c-tracking-[0.32px] c-text-danger-500 c-text-sm xl:c-text-base 2xl:c-text-lg"
                >
                  {errors?.gender.message}
                </span>
              )}
            </div>
          )}
        </div>

        <div className={twMerge('c-flex c-gap-8 xl:c-gap-10 2xl:c-gap-14 c-mb-9 xl:c-mb-3 2xl:c-mb-5')}>
          {isToBeDisplayed(EPatientFormField.FIRSTNAME) && (
            <div className="c-w-[300px] xl:c-w-80">
              <CurecallInput
                {...register('firstname', {
                  required: t('components.patient.add-or-edit.patient-form.input-firstname-error-message'),
                })}
                onBlur={() => setValue('firstname', capitalizeFirstname(watch('firstname')))}
                label={t('components.patient.add-or-edit.patient-form.input-firstname-label')}
                name="firstname"
                tooltipContent={TooltipFirstnameContent()}
                tooltipPosition="right"
                tooltipContentClassName="tw-w-[300px]"
                placeholder={t('components.patient.add-or-edit.patient-form.input-firstname-placeholder')}
                errorMessage={errors?.firstname?.message}
                isRequired={isRequiredField(EPatientFormField.FIRSTNAME)}
              />
            </div>
          )}
          {isToBeDisplayed(EPatientFormField.LASTNAME) && (
            <div className="c-w-[300px] xl:c-w-80">
              <CurecallInput
                {...register('lastname', {
                  required: t('components.patient.add-or-edit.patient-form.input-lastname-error-message'),
                })}
                onBlur={() => setValue('lastname', watch('lastname').toUpperCase())}
                label={t('components.patient.add-or-edit.patient-form.input-lastname-label')}
                placeholder={t('components.patient.add-or-edit.patient-form.input-lastname-placeholder')}
                errorMessage={errors?.lastname?.message}
                isRequired={isRequiredField(EPatientFormField.LASTNAME)}
              />
            </div>
          )}
          {isToBeDisplayed(EPatientFormField.BIRTH_DATE) && (
            <div className="c-w-[300px] xl:c-w-80">
              <CurecallDatePicker
                {...register('birthdate', {
                  required: t('components.patient.add-or-edit.patient-form.input-birthdate-error-message'),
                })}
                localeLanguage={navigator.language}
                label={t('components.patient.add-or-edit.patient-form.input-birthdate-label')}
                onChangeDate={(date: string | null) => {
                  if (date) {
                    setValue('birthdate', date)
                    setSelectedDate(date)
                  }
                  clearErrors('birthdate')
                }}
                isRequired={isRequiredField(EPatientFormField.BIRTH_DATE)}
                withoutCalendarPicker={true}
                defaultValue={selectedDate}
                errorMessage={errors?.birthdate?.message}
              />
            </div>
          )}
        </div>

        <div className={twMerge('c-flex c-gap-8 xl:c-gap-10 2xl:c-gap-14 c-mb-9 xl:c-mb-3 2xl:c-mb-5')}>
          {isToBeDisplayed(EPatientFormField.LOCALE) && (
            <div className="c-w-[300px] xl:c-w-80">
              <CurecallSelect
                {...register('locale', {
                  required: t(
                    'components.patient.add-or-edit.patient-form.select-patient-locale-error-message',
                  ),
                })}
                label={t('components.patient.add-or-edit.patient-form.select-patient-locale-label')}
                items={patientLocaleOptions}
                onChange={handleChangePatientLocale}
                selectedItem={selectedPatientLocale}
                isRequired={isRequiredField(EPatientFormField.LOCALE)}
                placeholder={t(
                  'components.patient.add-or-edit.patient-form.select-patient-locale-placeholder',
                )}
                errorMessage={errors?.['locale'] && errors.locale.message}
                buttonClassName="tw-text-base"
              />
            </div>
          )}
          {isToBeDisplayed(EPatientFormField.MEDICAL_STAFF_MEMBER) && (
            <div className="c-w-[300px] xl:c-w-80">
              <SelectMedicalStaffMember
                name="MedicalStaffMemberId"
                projectId={projectId}
                isRequired={isRequiredField(EPatientFormField.MEDICAL_STAFF_MEMBER, false)}
                label={t('components.patient.add-or-edit.patient-form.select-medical-staff-member-label')}
                placeholder={t(
                  'components.patient.add-or-edit.patient-form.select-medical-staff-member-placeholder',
                )}
                customErrorMessage={t(
                  'components.patient.add-or-edit.patient-form.select-medical-staff-member-error-message',
                )}
              />
            </div>
          )}
          {isToBeDisplayed(EPatientFormField.PHONE) && (
            <div className="c-w-[300px] xl:c-w-80">
              <CurecallPhoneInput
                {...register('phone')}
                name="phone"
                label={t('components.patient.add-or-edit.patient-form.input-phone-label')}
                isRequired={isRequiredField(EPatientFormField.PHONE)}
                onChange={() => {
                  clearErrors('phone')
                }}
                defaultValue={defaultValues?.phone as any}
                placeholder={t('components.patient.add-or-edit.patient-form.input-phone-placeholder')}
                notValidPhoneErrorMessage={t(
                  'components.patient.add-or-edit.patient-form.input-phone-invalid-error-message',
                )}
                notCompletePhoneNumberErrorMessage={t(
                  'components.patient.add-or-edit.patient-form.input-phone-incomplete-error-message',
                )}
                errorMessage={errors?.phone?.message}
              />
            </div>
          )}

          {isToBeDisplayed(EPatientFormField.REMOTE_ID) &&
            project?.settings?.patientCreationFields?.length && (
              <div className="c-w-[300px] xl:c-w-80">
                <CurecallInput
                  {...register('remoteId', {
                    required: t(
                      'components.patient.add-or-edit.patient-form.input-remote-id-invalid-error-message',
                    ),
                  })}
                  label={t('components.patient.add-or-edit.patient-form.input-remote-id-label')}
                  name="remoteId"
                  placeholder={t('components.patient.add-or-edit.patient-form.input-remote-id-placeholder')}
                  errorMessage={errors?.lastname?.message}
                  isRequired={isRequiredField(EPatientFormField.REMOTE_ID)}
                />
              </div>
            )}
        </div>
        <div className="c-flex c-flex-col c-self-start c-h-12 c-mb-6">
          <button
            className="focus:c-outline-0 c-flex c-items-center c-font-nexa c-text-sm c-tracking-tighter xl:c-tracking-tight c-cursor-pointer"
            type="button"
            onClick={() => setDisplayCareGiver(!displayCareGiver)}
          >
            {t('components.patient.add-or-edit.patient-form.display-caregiver', {
              display: displayCareGiver,
            })}
            <span
              className={twMerge(
                'c-cursor-pointer arrow-right c-ml-2',
                displayCareGiver ? `arrow-right-patient arrow-toggle` : `arrow-right-patient`,
              )}
            ></span>
          </button>
          {displayCareGiver && (
            <div className="c-flex c-mt-1 c-space-x-2 c-text-lg c-tracking-[0.36px] c-font-light c-relative">
              <CurecallToggle
                size={ECurecallSize.LARGE}
                isEnabled={!!hasCaregiver}
                onChange={() => setHasCareGiver && setHasCareGiver((prev) => !prev)}
              />
              <div className="c-text-sm lg:c-text-base xl:c-text-lg">
                {t('components.patient.add-or-edit.patient-form.is-caregiver-phone')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PatientFormComponent
